'use client'

import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import 'dayjs/locale/en'
import { ChevronLeft } from 'react-feather'
import { useLocale, useTranslations } from '@/lib/next-shim'

import { objectTypes } from '@/core/api/modules/document'
import { GetMyInfosQuery } from '@/core/graphql/types'
import { DocumentsSection } from '@/features/documents'

import { ConsultationInfo } from './ConsultationInfo'
import { FormSubmissionsSection } from './FormSubmissionsSection'
import { Notes } from './Notes'
import { VitalSigns } from './VitalSigns'

type FileData = {
  filename: string
  key: string
  url: string
}

type ConsultationDetailsProps = {
  consultation: GetMyInfosQuery['myInfos']['consultations'][number]
  consultationFiles: FileData[]
  consultationFormSubmissions: GetMyInfosQuery['myInfos']['formSubmissions']
  documentsTitle: string
  objectType: (typeof objectTypes)[number]
  objectId: string
  onClose: () => void
}

export function ConsultationDetails({
  consultation,
  consultationFiles,
  consultationFormSubmissions,
  documentsTitle,
  objectType,
  objectId,
  onClose,
}: ConsultationDetailsProps) {
  const t = useTranslations()
  const lang = useLocale()

  const doctorName = consultation.medecin ? `Dr. ${consultation.medecin.userInfo.surname} ${consultation.medecin.userInfo.name}` : null
  const speciality = consultation.medecin?.speciality
  const formattedDate = dayjs(consultation.startDate).locale(lang).format('dddd D MMMM YYYY')

  const title = doctorName ?? formattedDate
  const subtitleParts = [speciality, doctorName ? formattedDate : null].filter(Boolean)

  return (
    <div className="flex w-full flex-col gap-6">
      <button
        type="button"
        onClick={onClose}
        className="inline-flex w-fit items-center gap-1 rounded-md text-sm font-medium text-gray-600 transition-colors hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        <ChevronLeft size={18} aria-hidden="true" />
        {t('common.goBack')}
      </button>

      <header className="flex flex-col gap-1 border-b border-gray-200 pb-4">
        <h1 className="text-xl font-bold capitalize text-gray-900 md:text-2xl">{title}</h1>
        {subtitleParts.length > 0 && <p className="text-sm capitalize text-gray-500">{subtitleParts.join(' · ')}</p>}
      </header>

      <div className="flex flex-col gap-6">
        <ConsultationInfo consultation={consultation} />
        <DocumentsSection files={consultationFiles} title={documentsTitle} objectType={objectType} showUploadButton={false} objectId={objectId} flush />
        <VitalSigns consultation={consultation} />
        <Notes consultation={consultation} />
        <FormSubmissionsSection submissions={consultationFormSubmissions} contextType="consultation" contextId={objectId} />
      </div>
    </div>
  )
}
